import { Vue, Component } from "vue-property-decorator";
import { BCard, BButton, BListGroup, BListGroupItem, BCardText } from "bootstrap-vue";
import { ICompany, INotificationChannel, NotificationChannelType } from "@lib";
import { AltCheckBox } from "@/core/alt-ui";
import AltIcon from "@/core/components/alt-icon";
import { ModalComponent } from "@core/components/alt-ui/modal";
import { NotificationTelegramBotModal } from "./modals/notification-telegram-bot.modal";
import { NotificationSubscriptionsModal } from "./modals/notification-subscriptions.modal";
import { ObjectUtils } from "@/utils/object.utils";

@Component({
    components: {
        BCard,
        BCardText,
        BButton,
        BListGroup,
        BListGroupItem,
        AltCheckBox,
        AltIcon,
        ModalComponent,
    },
})
export default class SettingsCompanyNotifications extends Vue {
    private company: ICompany | null = null;

    public channels: INotificationChannel[] = [];

    public telegramBotModal = new NotificationTelegramBotModal();
    public subscriptionsModal = new NotificationSubscriptionsModal();

    public get notificationsVisible(): boolean {
        return this.$alt.config.common.notificationsEnabled;
    }

    public get channelTypes(): NotificationChannelType[] {
        return [NotificationChannelType.TelegramCustomBot];
    }

    public get isHelpVisible(): boolean {
        return this.$alt.config.common.documentationEnabled;
    }

    public async mounted(): Promise<void> {
        await this.$alt.longOperation(async () => {
            this.company = await this.$info.getCompany();
            this.channels = await this.$alt.system.usecase.createNotificationChannelUseCase().select(this.company.id);
        });
    }

    public async refresh(): Promise<void> {
        await this.$alt.longOperation(async () => {
            if (!this.company) {
                return;
            }

            this.channels = await this.$alt.system.usecase.createNotificationChannelUseCase().select(this.company.id);
        });
    }

    public getChannelTypeString(type: NotificationChannelType): string {
        switch (type) {
            case NotificationChannelType.TelegramCustomBot:
                return "Telegram-бот";
        }

        return "";
    }

    public async createChannel(type: NotificationChannelType): Promise<void> {
        // TODO: показывать модалку в зависимости от type

        let channel: INotificationChannel | null = null;

        await this.telegramBotModal.show({
            createHandler: dto =>
                this.$alt.longOperation(async () => {
                    if (!this.company) {
                        return;
                    }

                    channel = await this.$alt.system.usecase
                        .createNotificationChannelUseCase()
                        .create(this.company.id, dto);

                    this.channels.push(channel);
                }, "Telegram бот успешно добавлен."),
        });

        if (channel) {
            this.showChannelSubscriptions(channel);
        }
    }

    public async updateChannel(channel: INotificationChannel): Promise<void> {
        // TODO: показывать модалку в зависимости от type

        await this.telegramBotModal.show({
            channel,
            updateHandler: (channelId, dto) =>
                this.$alt.longOperation(async () => {
                    if (!this.company) {
                        return;
                    }

                    const channelUpdated = await this.$alt.system.usecase
                        .createNotificationChannelUseCase()
                        .update(this.company.id, channelId, dto);

                    ObjectUtils.replaceArrayItem(this.channels, channelUpdated, c => c.id === channel.id);
                }, "Telegram бот успешно изменён."),
        });
    }

    public async showChannelSubscriptions(channel: INotificationChannel): Promise<void> {
        await this.subscriptionsModal.show({
            channel,
            saveHandler: (channelId, dto) =>
                this.$alt.longOperation(async () => {
                    if (!this.company) {
                        return;
                    }

                    const channelUpdated = await this.$alt.system.usecase
                        .createNotificationChannelUseCase()
                        .update(this.company.id, channelId, dto);

                    ObjectUtils.replaceArrayItem(this.channels, channelUpdated, c => c.id === channel.id);
                }, "Уведомления успешно сохранены."),
        });
    }

    public async confirmEnableChannel(channel: INotificationChannel): Promise<void> {
        const confirm = await this.$alt.message.confirm(
            `Вы уверены, что хотите включить уведомления '${channel.name}'?`,
            "Включение уведомлений",
            { okText: "Включить" },
        );

        if (!confirm) {
            return;
        }

        await this.$alt.longOperation(async () => {
            if (!this.company) {
                return;
            }

            const channelUpdated = await this.$alt.system.usecase
                .createNotificationChannelUseCase()
                .enable(this.company.id, channel.id);

            ObjectUtils.replaceArrayItem(this.channels, channelUpdated, c => c.id === channel.id);
        });
    }

    public async confirmDisableChannel(channel: INotificationChannel): Promise<void> {
        const confirm = await this.$alt.message.confirm(
            `Вы уверены, что хотите отключить уведомления '${channel.name}'?`,
            "Отключение уведомлений",
            { okText: "Отключить" },
        );

        if (!confirm) {
            return;
        }

        await this.$alt.longOperation(async () => {
            if (!this.company) {
                return;
            }

            const channelUpdated = await this.$alt.system.usecase
                .createNotificationChannelUseCase()
                .disable(this.company.id, channel.id);

            ObjectUtils.replaceArrayItem(this.channels, channelUpdated, c => c.id === channel.id);
        });
    }

    public async confirmDeleteChannel(channel: INotificationChannel): Promise<void> {
        const confirm = await this.$alt.message.confirm(
            `Вы уверены, что хотите удалить уведомления '${channel.name}'?`,
            "Удаление уведомлений",
            { okText: "Удалить" },
        );

        if (!confirm) {
            return;
        }

        await this.$alt.longOperation(async () => {
            if (!this.company) {
                return;
            }

            await this.$alt.system.usecase.createNotificationChannelUseCase().delete(this.company.id, channel.id);

            ObjectUtils.deleteArrayItem(this.channels, c => c.id === channel.id);
        });
    }

    public clickHelp(): void {
        const link = `${this.$alt.config.url.docs}guide/features/notifications/`;
        window.open(link, "_blank")?.focus();
    }
}
